import React, { useContext, useState } from "react";
import { Box, FormControl, TextField, useTheme, Collapse } from "@mui/material";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { MinorUserContext } from "../../context/UserNameProvider";
import Question from "../../components/Question";
import { continueButtonSx, inputLabelSx, textAreaSx } from "../../lib/uiformatting";
import ContinueButton from "../../components/ContinueButton";
import MoveButton from "../../components/MoveButton";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";

import {
  setMedicalHistoryChanges as updateMedicalHistoryChanges,
  setNewMedicationsSinceLastVisit as updateNewMedicationsSinceLastVisit,
} from "../../redux/userSlice";

export default function MedicalHistoryUpdates() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

  const { firstName, isMinor } = useContext(MinorUserContext);
  const user = useSelector((state) => state.user);

  const existingHistory = user.medicalHistoryChanges ?? { has: "", description: "" };
  const existingNewMeds = user.newMedicationsSinceLastVisit ?? { has: "", description: "" };

  const [historyYesNo, setHistoryYesNo] = useState(existingHistory.has || "");
  const [historyDesc, setHistoryDesc] = useState(existingHistory.description || "");
  const [historyError, setHistoryError] = useState(false);

  const [newMedsYesNo, setNewMedsYesNo] = useState(existingNewMeds.has || "");
  const [newMedsDesc, setNewMedsDesc] = useState(existingNewMeds.description || "");
  const [newMedsError, setNewMedsError] = useState(false);

  const yesNoOptions = {
    Yes: "y",
    No: "n",
  };

  const handleHistoryYesNo = (value) => {
    setHistoryYesNo(value);
    if (value === "n") {
      setHistoryDesc("");
      setHistoryError(false);
    }
  };

  const handleNewMedsYesNo = (value) => {
    setNewMedsYesNo(value);
    if (value === "n") {
      setNewMedsDesc("");
      setNewMedsError(false);
    }
  };

  const handleHistoryDesc = (e) => {
    const v = e.target.value;
    setHistoryDesc(v);
    setHistoryError(v.trim() === "");
  };

  const handleNewMedsDesc = (e) => {
    const v = e.target.value;
    setNewMedsDesc(v);
    setNewMedsError(v.trim() === "");
  };

  const isContinueDisabled =
    historyYesNo === "" ||
    (historyYesNo === "y" && historyDesc.trim() === "") ||
    newMedsYesNo === "" ||
    (newMedsYesNo === "y" && newMedsDesc.trim() === "");

  const handleNext = () => {
    dispatch(updateMedicalHistoryChanges({ has: historyYesNo, description: historyDesc }));
    dispatch(updateNewMedicationsSinceLastVisit({ has: newMedsYesNo, description: newMedsDesc }));

    setPageAndDeferNavigate(dispatch, "/missed-doses", navigate);
  };

  const handleBack = () => {
    setPageAndDeferNavigate(dispatch, "/working-well", navigate);
  };

  return (
    <Question
      onBack={handleBack}
      title={`${isMinor ? `Has ${firstName}` : "Have you"} had any changes to your medical history?`}
      subtitle="Select Yes or No. If yes, please list the changes."
      input={
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Box
            sx={{
              width: "100%",
              maxWidth: 640,
              px: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 420,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: 2,
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                }}
              >
                {Object.keys(yesNoOptions).map((label) => (
                  <MoveButton
                    key={`history-${yesNoOptions[label]}`}
                    onClick={() => handleHistoryYesNo(yesNoOptions[label])}
                    title={label}
                    selected={historyYesNo === yesNoOptions[label]}
                  />
                ))}
              </Box>

              <Collapse in={historyYesNo === "y"} mountOnEnter unmountOnExit sx={{ width: "100%" }}>
                <FormControl sx={{ width: "100%" }} required={historyYesNo === "y"}>
                  <TextField
                    label="Please list changes"
                    variant="outlined"
                    color="secondary"
                    sx={{
                      ...textAreaSx,
                      width: "100%",
                      "& .MuiInputLabel-root": {
                        backgroundColor: theme.palette.background.paper,
                        padding: "0 32px",
                        transition: "all 0.2s ease-in-out",
                      },
                    }}
                    autoComplete="off"
                    value={historyDesc}
                    multiline
                    rows={2}
                    onChange={handleHistoryDesc}
                    error={historyError}
                    helperText={historyError ? "Please list the changes." : ""}
                    InputLabelProps={{
                      sx: {
                        ...inputLabelSx,
                        "& .MuiInputLabel-shrink": {
                          backgroundColor: theme.palette.background.paper,
                        },
                      },
                    }}
                  />
                </FormControl>
              </Collapse>
            </Box>

            <Box
              sx={{
                width: "100%",
                maxWidth: 420,
                mt: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box sx={{ width: "100%", fontWeight: 600, textAlign: "center" }}>
                {`${isMinor ? `Has ${firstName}` : "Have you"} started any new medications since last visit?`}
              </Box>

              <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "1rem" }}>
                {Object.keys(yesNoOptions).map((label) => (
                  <MoveButton
                    key={`newmeds-${yesNoOptions[label]}`}
                    onClick={() => handleNewMedsYesNo(yesNoOptions[label])}
                    title={label}
                    selected={newMedsYesNo === yesNoOptions[label]}
                  />
                ))}
              </Box>

              <Collapse in={newMedsYesNo === "y"} mountOnEnter unmountOnExit sx={{ width: "100%" }}>
                <FormControl sx={{ width: "100%" }} required={newMedsYesNo === "y"}>
                  <TextField
                    label="Please mention the medication(s)"
                    variant="outlined"
                    color="secondary"
                    sx={{
                      ...textAreaSx,
                      width: "100%",
                      "& .MuiInputLabel-root": {
                        backgroundColor: theme.palette.background.paper,
                        padding: "0 32px",
                        transition: "all 0.2s ease-in-out",
                      },
                    }}
                    autoComplete="off"
                    value={newMedsDesc}
                    multiline
                    rows={2}
                    onChange={handleNewMedsDesc}
                    error={newMedsError}
                    helperText={newMedsError ? "Please enter the new medication(s)." : ""}
                    InputLabelProps={{
                      sx: {
                        ...inputLabelSx,
                        "& .MuiInputLabel-shrink": {
                          backgroundColor: theme.palette.background.paper,
                        },
                      },
                    }}
                  />
                </FormControl>
              </Collapse>
            </Box>
          </Box>
        </Box>
      }
      footer={
        <Box sx={{ ...continueButtonSx }}>
          <ContinueButton
            onClick={handleNext}
            title="Continue"
            disabled={isContinueDisabled}
            selected={!isContinueDisabled}
          />
        </Box>
      }
    />
  );
}
